import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { type ClientPaginationProperties } from "./useClientPagination";
import {
  PAGE_SIZE_OPTIONS,
  type PageSize,
  type ServerPaginationProperties,
  pageInfoFragment,
  usePagination,
  type ResetPagination,
} from "./usePagination";
import { Paragraph } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type PaginationProperties = ServerPaginationProperties | ClientPaginationProperties;
export { pageInfoFragment, usePagination, type ResetPagination };

type Props = {
  pagination: PaginationProperties;
};

export function Pagination({
  pagination: {
    paginationInfo: { currentPage, totalPages, pageSize },
    controls: {
      handleGoToFirstPage,
      handleGoToNextPage,
      handleGoToLastPage,
      handleGoToPreviousPage,
      handleSetPageSize,
    },
    flags: { isNextPageDisabled, isPreviousPageDisabled },
  },
}: Props) {
  return (
    <div className="flex items-center flex-wrap gap-2">
      <Paragraph className="my-0 text-sm font-medium">Rows per page:</Paragraph>
      <Select
        onValueChange={(value: PageSize) => {
          handleSetPageSize(value);
        }}
        value={pageSize}
      >
        <SelectTrigger className="w-20 h-9 text-foreground border-border" aria-label="Rows per page">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PAGE_SIZE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Paragraph className="my-0 text-sm font-medium">{`Page ${currentPage} of ${totalPages}`}</Paragraph>
      <div>
        <Button
          variant="outline"
          size="icon"
          disabled={isPreviousPageDisabled}
          onClick={handleGoToFirstPage}
          title="First page"
          className="mr-1"
        >
          <ChevronsLeft />
        </Button>

        <Button
          variant="outline"
          size="icon"
          title="Previous page"
          disabled={isPreviousPageDisabled}
          onClick={handleGoToPreviousPage}
          className="mr-1"
        >
          <ChevronLeft />
        </Button>
      </div>

      <div>
        <Button
          variant="outline"
          size="icon"
          title="Next page"
          onClick={handleGoToNextPage}
          disabled={isNextPageDisabled}
        >
          <ChevronRight />
        </Button>
        <Button
          variant="outline"
          size="icon"
          disabled={isNextPageDisabled}
          onClick={handleGoToLastPage}
          title="Last page"
          className="ml-1"
        >
          <ChevronsRight />
        </Button>
      </div>
    </div>
  );
}
